import styled from "@emotion/styled";
import { Button, Tooltip, Typography } from "@mui/material";
import CardIcon from "@mui/icons-material/CreditCard";
import CheckIcon from "@mui/icons-material/Check";
import { CreditCardIcon } from "../CreditCardIcon";
import { SubscriptionStatusDetailsPaymentMethodStatusEnum } from "@shared/apis/sidekick/generated";
import { useTheme } from "@avenue-8/sidekick-ui";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useRemoveQueryParam } from "@shared/hooks/useRemoveQueryParam";

type CreditCardPaymentMethod = {
  type: "card";
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  status: SubscriptionStatusDetailsPaymentMethodStatusEnum;
  billingDetails: {
    address: {
      city: string;
      country: string;
      line1: string;
      line2?: string;
      zipCode: string;
      state: string;
    };
  };
};

export type PaymentMethods = CreditCardPaymentMethod[];

type Props = {
  paymentMethods: PaymentMethods;
  requiresPaymentMethod: boolean;
  onAddPaymentMethodClick: () => void;
  onEditPaymentMethodClick: (paymentMethod: CreditCardPaymentMethod) => void;
  readOnly?: boolean;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const PaymentMethodItem = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto) 1fr;
  align-items: center;
  gap: 2rem;
  padding: 1.25rem 1.75rem;
  border-radius: 0.75rem;
  min-height: 4.75rem;
  border: solid 1px ${(p) => p.theme.colors.border2};

  p {
    font-size: ${(p) => p.theme.text.fontSizes.m};
  }

  div {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  ${(p) => p.theme.media.mobileDown} {
    padding: 1.5rem 1.75rem 1rem 1.75rem;
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .credit-card-numbers:before {
    /* content: "•••• •••• •••• "; */
    content: "•••• ";
    letter-spacing: 0.125rem;

    ${(p) => p.theme.media.mobileDown} {
      content: "•••• ";
    }
  }

  .valid-thru {
    font-size: ${(p) => p.theme.text.fontSizes.m};
  }
`;

const BrandItem = styled(Typography)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: capitalize;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  /* margin-right: -0.75rem; */
`;

const formatExpiryDate = (expiryMonth: number, expiryYear: number): string => {
  const formattedMonth = expiryMonth.toString().padStart(2, "0");
  const formattedYear = expiryYear.toString().slice(-2);
  return `${formattedMonth}/${formattedYear}`;
};

export const PaymentMethodsList = (props: Props) => {
  const {
    paymentMethods,
    onAddPaymentMethodClick,
    onEditPaymentMethodClick,
    requiresPaymentMethod,
    readOnly,
  } = props;

  const [search] = useSearchParams();
  const removeQueryParam = useRemoveQueryParam(["add-payment-method-guide"]);

  const paymentMethodGuide = search.get("add-payment-method-guide");
  const displayAddPaymentMethodTooltip = paymentMethodGuide === "true";

  const handleAddPaymentMethodClick = () => {
    if (paymentMethodGuide) {
      removeQueryParam();
    }
    onAddPaymentMethodClick();
  };

  return (
    <Container>
      {paymentMethods.length === 0 && requiresPaymentMethod && (
        <PaymentMethodItem>
          <BrandItem>
            <CardIcon />
          </BrandItem>
          <Typography>No payment method added</Typography>
          <Typography />
          <Typography />
          <ActionsContainer>
            {!readOnly && (
              <Tooltip
                title={
                  displayAddPaymentMethodTooltip
                    ? "Click here to add your payment method"
                    : ""
                }
                open={displayAddPaymentMethodTooltip}
                arrow
                placement="top"
              >
                <Button
                  size="small"
                  // variant="outlined"
                  // startIcon={<AddIcon />}
                  onClick={handleAddPaymentMethodClick}
                >
                  Update
                </Button>
              </Tooltip>
            )}
            {readOnly && <Typography />}
          </ActionsContainer>
        </PaymentMethodItem>
      )}

      {paymentMethods.length === 0 && !requiresPaymentMethod && (
        <PaymentMethodItem>
          <BrandItem>
            <CheckIcon color="success" />
          </BrandItem>
          <Typography>No payment method is required</Typography>
          <Typography />
          <Typography />
        </PaymentMethodItem>
      )}

      {paymentMethods.map((paymentMethod) => (
        <PaymentMethodItem
          key={`${paymentMethod.brand}-${paymentMethod.last4}`}
        >
          <BrandItem>
            <CreditCardIcon brand={paymentMethod.brand} />
          </BrandItem>
          <Typography className="credit-card-numbers">
            {paymentMethod.last4}
          </Typography>
          <Typography className="valid-thru">
            Expires{" "}
            {formatExpiryDate(paymentMethod.expMonth, paymentMethod.expYear)}
          </Typography>
          <PaymentStatus status={paymentMethod.status} />
          <ActionsContainer>
            {!readOnly && (
              <Button
                size="small"
                // variant="outlined"
                onClick={() => onEditPaymentMethodClick(paymentMethod)}
              >
                Update
              </Button>
            )}
            {readOnly && <Typography />}
          </ActionsContainer>
        </PaymentMethodItem>
      ))}
    </Container>
  );
};

const PaymentStatusStyled = styled.span`
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: ${(p) => p.theme.text.fontSizes.s};
  font-weight: 500;
  justify-self: flex-start;
`;

const PaymentStatus = ({
  status,
}: {
  status: SubscriptionStatusDetailsPaymentMethodStatusEnum;
}) => {
  const theme = useTheme();

  const { color, backgroundColor } = useMemo(() => {
    switch (status) {
      case "Valid":
        return {
          color: theme.colors.success,
          backgroundColor: `${theme.colors.success}33`,
        };
      case "Pending":
        return {
          color: theme.colors.warning,
          backgroundColor: theme.name === "light" ? `#FFF6D2` : `#876d045c`,
        };
      case "Expired":
      case "Missing":
      case "Rejected":
      default:
        return {
          color: theme.colors.error,
          backgroundColor: `${theme.colors.error}33`,
        };
    }
  }, [theme.colors, theme.name, status]);

  return (
    <PaymentStatusStyled style={{ color, backgroundColor }}>
      {status}
    </PaymentStatusStyled>
  );
};
